import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { masterDataAPI, warehouseAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Plus, Upload, Download, Trash2, Edit, RotateCcw, Database, Search, Check, Eye
} from 'lucide-react';

const ProductMaster = () => {
  const { toast } = useToast();
  const getErrMsg = (error) => {
    const d = error.response?.data?.detail;
    if (!d) return 'Operation failed';
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(e => e.msg || JSON.stringify(e)).join('; ');
    return String(d);
  };
  const fileRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showView, setShowView] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  const [form, setForm] = useState({
    description: '', merv_rating: '8', price_per_unit: '',
    filter_size: '', inches: '1', default_warehouse_id: '', min_order_quantity: '1',
    display_on_homepage: true
  });
  const [formLoading, setFormLoading] = useState(false);

  const generateDescription = (filterSize) => {
    if (!filterSize) return '';
    const size = filterSize.toUpperCase().replace(/\s/g, '');
    const parts = size.split('X');
    if (parts.length === 3) {
      const w = parseFloat(parts[0]), h = parseFloat(parts[1]), d = parseFloat(parts[2]);
      if (!isNaN(w) && !isNaN(h) && !isNaN(d)) {
        const fmt = (v) => Number.isInteger(v) ? v.toString() : v.toString();
        return `${size} Disposable Filter (Actual Size: ${fmt(w - 0.5)}x${fmt(h - 0.5)}x${fmt(d - 0.25)})`;
      }
    }
    return `${size} Disposable Filter`;
  };

  const handleFilterSizeChange = (value) => {
    setForm(prev => ({
      ...prev,
      filter_size: value,
      description: generateDescription(value),
    }));
  };

  const load = async () => {
    try {
      const [data, whData] = await Promise.all([
        masterDataAPI.getProducts(),
        warehouseAPI.getAll(),
      ]);
      setProducts(data);
      setWarehouses(whData);
    } catch {
      toast({ title: 'Error', description: 'Failed to load products', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => setForm({
    description: '', merv_rating: '8', price_per_unit: '',
    filter_size: '', inches: '1', default_warehouse_id: '', min_order_quantity: '1',
    display_on_homepage: true
  });

  const handleCreate = async () => {
    if (!form.filter_size || !form.price_per_unit) {
      toast({ title: 'Required', description: 'Filter Size and Price are required', variant: 'destructive' });
      return;
    }
    setFormLoading(true);
    try {
      await masterDataAPI.createProduct({
        description: form.description || 'Disposable filters',
        merv_rating: parseInt(form.merv_rating),
        price_per_unit: parseFloat(form.price_per_unit),
        filter_size: form.filter_size,
        inches: parseInt(form.inches),
        default_warehouse_id: form.default_warehouse_id,
        min_order_quantity: parseInt(form.min_order_quantity) || 1,
        display_on_homepage: form.display_on_homepage,
      });
      toast({ title: 'Created', description: 'Product created successfully' });
      setShowAdd(false);
      resetForm();
      load();
    } catch (error) {
      toast({ title: 'Error', description: getErrMsg(error), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.price_per_unit) {
      toast({ title: 'Required', description: 'Price is required', variant: 'destructive' });
      return;
    }
    setFormLoading(true);
    try {
      await masterDataAPI.updateProduct(showEdit, {
        description: form.description,
        merv_rating: parseInt(form.merv_rating),
        price_per_unit: parseFloat(form.price_per_unit),
        filter_size: form.filter_size,
        inches: parseInt(form.inches),
        default_warehouse_id: form.default_warehouse_id,
        min_order_quantity: parseInt(form.min_order_quantity) || 1,
        display_on_homepage: form.display_on_homepage,
      });
      toast({ title: 'Updated', description: `Product ${showEdit} updated` });
      setShowEdit(null);
      resetForm();
      load();
    } catch (error) {
      toast({ title: 'Error', description: getErrMsg(error), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await masterDataAPI.deleteProduct(showDeleteConfirm);
      toast({ title: 'Deleted', description: `Product ${showDeleteConfirm} flagged as deleted` });
      setShowDeleteConfirm(null);
      load();
    } catch (error) {
      toast({ title: 'Error', description: getErrMsg(error), variant: 'destructive' });
    }
  };

  const handleRestore = async (pid) => {
    try {
      await masterDataAPI.restoreProduct(pid);
      toast({ title: 'Restored', description: `Product ${pid} restored` });
      load();
    } catch (error) {
      toast({ title: 'Error', description: getErrMsg(error), variant: 'destructive' });
    }
  };

  const handleSelectPrice = async (productId, recordId) => {
    try {
      await masterDataAPI.selectPrice(productId, recordId);
      toast({ title: 'Price Selected' });
      load();
    } catch (error) {
      toast({ title: 'Error', description: getErrMsg(error), variant: 'destructive' });
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await masterDataAPI.uploadProducts(file);
      setUploadResult(result);
      toast({ title: 'Upload Complete', description: result.message });
      load();
    } catch (error) {
      toast({ title: 'Upload Failed', description: getErrMsg(error), variant: 'destructive' });
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const templateUrl = `${process.env.REACT_APP_BACKEND_URL}/api/master-data/products/template/download`;

  const handleCopyLink = () => {
    try {
      // Fallback copy method that works everywhere
      const textarea = document.createElement('textarea');
      textarea.value = templateUrl;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast({ title: 'Copied', description: 'Download link copied to clipboard' });
    } catch {
      toast({ title: 'Link', description: templateUrl });
    }
  };

  const openEdit = (product) => {
    const active = product.active_record || product.records[0];
    setForm({
      description: active.description || 'Disposable filters',
      merv_rating: String(active.merv_rating),
      price_per_unit: String(active.price_per_unit),
      filter_size: active.filter_size || '',
      inches: String(active.inches ?? 1),
      default_warehouse_id: active.default_warehouse_id || '',
      min_order_quantity: String(active.min_order_quantity ?? 1),
      display_on_homepage: active.display_on_homepage !== false,
    });
    setShowEdit(product.product_id);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '-';

  const filtered = products.filter(p => {
    const active = p.active_record || p.records?.[0];
    const isDeleted = p.records?.every(r => r.deletion_flag);
    if (!showDeleted && isDeleted) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return p.product_id.toLowerCase().includes(q) ||
      active?.description?.toLowerCase().includes(q) ||
      active?.filter_size?.toLowerCase().includes(q) ||
      String(active?.merv_rating).includes(q);
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
        </div>
      </Layout>
    );
  }

  const viewActive = showView?.active_record || showView?.records?.[0];

  return (
    <Layout>
      <div className="space-y-4" data-testid="product-master-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
              <Database className="h-6 w-6 text-sky-600" /> Product Master Data
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{filtered.length} products</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => { resetForm(); setShowAdd(true); }}
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
              data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Product
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowDownloadDialog(true)}
              data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-1" /> Template
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-btn">
              <Upload className="h-4 w-4 mr-1" /> Upload Excel
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileUpload} />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search by ID, filter size, MERV..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-sm" data-testid="search-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-gray-300" data-testid="show-deleted-checkbox" />
            Show deleted
          </label>
        </div>

        {/* Compact Table */}
        <div className="bg-white rounded-xl border shadow-sm overflow-x-auto" data-testid="products-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-3 py-2.5 text-left">Product ID</th>
                <th className="px-3 py-2.5 text-left">Filter Size</th>
                <th className="px-3 py-2.5 text-center">MERV</th>
                <th className="px-3 py-2.5 text-center">Inches</th>
                <th className="px-3 py-2.5 text-left">Description</th>
                <th className="px-3 py-2.5 text-right">Price</th>
                <th className="px-3 py-2.5 text-right">Cost</th>
                <th className="px-3 py-2.5 text-center">MOQ</th>
                <th className="px-3 py-2.5 text-left">Warehouse</th>
                <th className="px-3 py-2.5 text-center">Homepage</th>
                <th className="px-3 py-2.5 text-left">Changed</th>
                <th className="px-3 py-2.5 text-center w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="py-12 text-center text-gray-400">
                    <Database className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="font-medium text-sm">No products found</p>
                    <p className="text-xs mt-1">Add a product or upload an Excel file to get started.</p>
                  </td>
                </tr>
              ) : (
                filtered.map(product => {
                  const active = product.active_record || product.records?.[0];
                  if (!active) return null;
                  const isDeleted = product.records?.every(r => r.deletion_flag);
                  const isExpanded = expandedId === product.product_id;
                  const historyCount = product.records?.length || 0;

                  return (
                    <React.Fragment key={product.product_id}>
                      <tr className={`hover:bg-slate-50 transition-colors cursor-pointer ${isDeleted ? 'opacity-50 bg-red-50/30' : ''}`}
                        data-testid={`product-row-${product.product_id}`}
                        onClick={() => historyCount > 1 && setExpandedId(isExpanded ? null : product.product_id)}>
                        <td className="px-3 py-2 font-mono text-xs font-bold text-slate-800 whitespace-nowrap">
                          {product.product_id}
                          {isDeleted && <span className="ml-1 text-[10px] font-normal text-red-500 bg-red-50 px-1 py-0.5 rounded">DEL</span>}
                          {historyCount > 1 && <span className="ml-1 text-[10px] text-slate-400">({historyCount})</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-700 font-medium whitespace-nowrap">{active.filter_size || '-'}</td>
                        <td className="px-3 py-2 text-center text-slate-700">{active.merv_rating}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{active.inches ?? '-'}</td>
                        <td className="px-3 py-2 text-slate-500 max-w-[200px] truncate" title={active.description}>{active.description}</td>
                        <td className="px-3 py-2 text-right font-bold text-sky-700 whitespace-nowrap">${active.price_per_unit?.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-slate-500 whitespace-nowrap">{active.cost_per_unit ? `$${active.cost_per_unit.toFixed(2)}` : '-'}</td>
                        <td className="px-3 py-2 text-center text-slate-600">{active.min_order_quantity ?? 1}</td>
                        <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{active.default_warehouse_id || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block w-2 h-2 rounded-full ${active.display_on_homepage !== false ? 'bg-green-500' : 'bg-red-400'}`} />
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">{formatDate(active.changed_at)}</td>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-0.5">
                            <button onClick={() => setShowView(product)} className="p-1 rounded hover:bg-sky-50 text-sky-600"
                              data-testid={`view-btn-${product.product_id}`} title="View">
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            {!isDeleted ? (
                              <>
                                <button onClick={() => openEdit(product)} className="p-1 rounded hover:bg-sky-50 text-sky-600"
                                  data-testid={`edit-btn-${product.product_id}`} title="Edit">
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => setShowDeleteConfirm(product.product_id)} className="p-1 rounded hover:bg-red-50 text-red-500"
                                  data-testid={`delete-btn-${product.product_id}`} title="Delete">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => handleRestore(product.product_id)} className="p-1 rounded hover:bg-green-50 text-green-600"
                                data-testid={`restore-btn-${product.product_id}`} title="Restore">
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded price history */}
                      {isExpanded && historyCount > 1 && (
                        <tr>
                          <td colSpan={12} className="bg-slate-50 border-t px-4 py-3">
                            <p className="text-xs font-bold text-slate-500 uppercase mb-2">Price History</p>
                            <div className="space-y-1">
                              {product.records.map((rec) => (
                                <label key={rec.record_id}
                                  className={`flex items-center gap-3 px-3 py-1.5 rounded cursor-pointer text-sm border ${rec.is_active_price ? 'bg-sky-50 border-sky-200' : 'bg-white border-transparent hover:border-slate-200'}`}>
                                  <input type="radio" name={`price-${product.product_id}`}
                                    checked={rec.is_active_price}
                                    onChange={() => handleSelectPrice(product.product_id, rec.record_id)}
                                    className="accent-sky-600" data-testid={`radio-${rec.record_id}`} />
                                  <span className="font-bold text-slate-800 w-20">${rec.price_per_unit?.toFixed(2)}</span>
                                  <span className="text-xs text-slate-500 flex-1">{rec.description} - MERV {rec.merv_rating}</span>
                                  <span className="text-xs text-slate-400">{formatDate(rec.changed_at)}</span>
                                  {rec.is_active_price && (
                                    <span className="text-xs font-bold text-sky-600 bg-sky-100 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                      <Check className="h-3 w-3" /> Active
                                    </span>
                                  )}
                                </label>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Product</DialogTitle></DialogHeader>
          <DialogDescription>Product ID will be auto-generated from MERV rating + Filter Size.</DialogDescription>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>MERV Rating *</Label>
                <Select value={form.merv_rating} onValueChange={(v) => setForm({ ...form, merv_rating: v })}>
                  <SelectTrigger data-testid="form-merv"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8">MERV 8</SelectItem>
                    <SelectItem value="11">MERV 11</SelectItem>
                    <SelectItem value="13">MERV 13</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Filter Size *</Label>
                <Input value={form.filter_size} placeholder="e.g. 20x25x1"
                  onChange={(e) => handleFilterSizeChange(e.target.value)}
                  data-testid="form-filter-size" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inches *</Label>
                <Select value={form.inches} onValueChange={(v) => setForm({ ...form, inches: v })}>
                  <SelectTrigger data-testid="form-inches"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['1','2','3','4','5'].map(i => <SelectItem key={i} value={i}>{i}"</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Price Per Unit *</Label>
                <Input type="number" step="0.01" min="0.01" value={form.price_per_unit}
                  placeholder="0.00" onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })}
                  data-testid="form-price" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} placeholder="Auto-generated from filter size"
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="form-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Order Quantity</Label>
                <Input type="number" min="1" value={form.min_order_quantity}
                  onChange={(e) => setForm({ ...form, min_order_quantity: e.target.value })}
                  data-testid="form-moq" />
              </div>
              <div className="space-y-2">
                <Label>Default Warehouse</Label>
                <select className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                  value={form.default_warehouse_id}
                  onChange={(e) => setForm({ ...form, default_warehouse_id: e.target.value })}
                  data-testid="form-default-warehouse">
                  <option value="">-- None --</option>
                  {warehouses.map(wh => (
                    <option key={wh.warehouse_id} value={wh.warehouse_id}>{wh.warehouse_id} - {wh.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer" data-testid="form-display-homepage">
              <input type="checkbox" checked={form.display_on_homepage}
                onChange={(e) => setForm({ ...form, display_on_homepage: e.target.checked })}
                className="rounded border-gray-300 h-4 w-4 accent-sky-600" />
              <div>
                <p className="text-sm font-medium text-slate-800">Display on homepage and dropdown</p>
                <p className="text-xs text-slate-400">Show this product on the landing page size selector</p>
              </div>
            </label>
            {form.filter_size && (
              <p className="text-xs text-slate-400 bg-slate-50 p-2 rounded">
                Product ID: <strong>M{form.merv_rating}-{form.filter_size.toUpperCase()}</strong>
              </p>
            )}
            <Button onClick={handleCreate} disabled={formLoading}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500" data-testid="form-submit-btn">
              {formLoading ? 'Creating...' : 'Add Product'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Product - {showEdit}</DialogTitle>
            <DialogDescription>Changing the price creates a new price record. Previous prices are preserved.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>MERV Rating</Label>
                <Select value={form.merv_rating} onValueChange={(v) => setForm({ ...form, merv_rating: v })}>
                  <SelectTrigger data-testid="edit-merv"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8">MERV 8</SelectItem>
                    <SelectItem value="11">MERV 11</SelectItem>
                    <SelectItem value="13">MERV 13</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Filter Size</Label>
                <Input value={form.filter_size} placeholder="e.g. 20x25x1"
                  onChange={(e) => handleFilterSizeChange(e.target.value)}
                  data-testid="edit-filter-size" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inches</Label>
                <Select value={form.inches} onValueChange={(v) => setForm({ ...form, inches: v })}>
                  <SelectTrigger data-testid="edit-inches"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['1','2','3','4','5'].map(i => <SelectItem key={i} value={i}>{i}"</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Price Per Unit</Label>
                <Input type="number" step="0.01" min="0.01" value={form.price_per_unit}
                  onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })}
                  data-testid="edit-price" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} placeholder="Auto-generated from filter size"
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="edit-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Order Quantity</Label>
                <Input type="number" min="1" value={form.min_order_quantity}
                  onChange={(e) => setForm({ ...form, min_order_quantity: e.target.value })}
                  data-testid="edit-moq" />
              </div>
              <div className="space-y-2">
                <Label>Default Warehouse</Label>
                <select className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                  value={form.default_warehouse_id}
                  onChange={(e) => setForm({ ...form, default_warehouse_id: e.target.value })}
                  data-testid="edit-default-warehouse">
                  <option value="">-- None --</option>
                  {warehouses.map(wh => (
                    <option key={wh.warehouse_id} value={wh.warehouse_id}>{wh.warehouse_id} - {wh.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer" data-testid="edit-display-homepage">
              <input type="checkbox" checked={form.display_on_homepage}
                onChange={(e) => setForm({ ...form, display_on_homepage: e.target.checked })}
                className="rounded border-gray-300 h-4 w-4 accent-sky-600" />
              <div>
                <p className="text-sm font-medium text-slate-800">Display on homepage and dropdown</p>
                <p className="text-xs text-slate-400">Show this product on the landing page size selector</p>
              </div>
            </label>
            <Button onClick={handleUpdate} disabled={formLoading}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500" data-testid="edit-submit-btn">
              {formLoading ? 'Updating...' : 'Update Product'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!showView} onOpenChange={() => setShowView(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Product Details</DialogTitle></DialogHeader>
          {viewActive && (
            <div className="space-y-3" data-testid="view-dialog">
              <div className="grid grid-cols-2 gap-2">
                {[
                  ['Product ID', showView.product_id],
                  ['MERV Rating', `MERV ${viewActive.merv_rating}`],
                  ['Filter Size', viewActive.filter_size || '-'],
                  ['Inches', `${viewActive.inches ?? '-'}"`],
                  ['Price Per Unit', `$${viewActive.price_per_unit?.toFixed(2)}`],
                  ['Product Cost', viewActive.cost_per_unit ? `$${viewActive.cost_per_unit.toFixed(2)}` : '-'],
                  ['Description', viewActive.description],
                  ['Min Order Qty', viewActive.min_order_quantity ?? 1],
                  ['Warehouse', viewActive.default_warehouse_id || '-'],
                  ['Last Changed', formatDate(viewActive.changed_at)],
                  ['Homepage', viewActive.display_on_homepage !== false ? 'Yes' : 'No'],
                ].map(([label, val]) => (
                  <div key={label} className="bg-slate-50 p-2.5 rounded-lg">
                    <p className="text-[10px] text-slate-400 font-medium uppercase">{label}</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{val}</p>
                  </div>
                ))}
              </div>
              {showView.records?.length > 1 && (
                <div className="pt-2 border-t">
                  <p className="text-xs font-bold text-slate-500 mb-2">{showView.records.length} Price Records</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
            <DialogDescription>
              This will flag product <strong>{showDeleteConfirm}</strong> as deleted. Products with inventory stock cannot be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
              Delete Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Result Dialog */}
      <Dialog open={!!uploadResult} onOpenChange={() => setUploadResult(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Results</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-4">
              <div className="flex-1 p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{uploadResult?.created || 0}</p>
                <p className="text-xs text-green-600">Created</p>
              </div>
              <div className="flex-1 p-3 bg-sky-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-sky-700">{uploadResult?.updated || 0}</p>
                <p className="text-xs text-sky-600">Updated</p>
              </div>
            </div>
            {uploadResult?.errors?.length > 0 && (
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-xs font-bold text-red-700 mb-1">Errors</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {uploadResult.errors.map((err, i) => (
                    <p key={i} className="text-xs text-red-600">{err}</p>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={() => setUploadResult(null)} className="w-full" variant="outline">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Download Template Dialog */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download Product Template</DialogTitle>
            <DialogDescription>
              Use the link below to download the Excel template file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-center">
              <a
                href={templateUrl}
                download="product_master_template.xlsx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg font-bold text-sky-700 underline hover:text-sky-900 inline-flex items-center gap-2"
                data-testid="template-download-link"
              >
                <Download className="h-5 w-5" />
                product_master_template.xlsx
              </a>
              <p className="text-xs text-slate-500 mt-3">
                Click the link above. If the download doesn't start, <strong>right-click</strong> the link and select <strong>"Save link as..."</strong>
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleCopyLink} data-testid="copy-link-btn">
                Copy Download Link
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowDownloadDialog(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ProductMaster;
